<template>
	<v-row>
		<v-col>

			<v-form v-if="this.partner" v-model="formIsValid.place" ref="placeForm">
				<ui-box :form="$refs.placeForm" new-forms>

					<form-title title="Place"></form-title>

					<form-divider inner/>

					<v-row>
						<v-col>
							<venue-select
								v-model="checkin.venue_id"
								:rules="rules.place"
								:venues="venues"
							></venue-select>
						</v-col>
					</v-row>
				</ui-box>
			</v-form>

			<form-divider/>

			<v-form v-model="formIsValid.checkinType" ref="checkinTypeForm">

				<ui-box
						v-if="shouldShowSection('child_template')"
						:form="$refs.checkinTypeForm"
						:eager="!!product.id"
						new-forms
				>

					<form-title title="Check-In Type">
						What type of check-in validation would you like?
					</form-title>

					<form-divider inner/>

					<!-- Child Coupon Template -->
					<v-row>
						<v-col>
							<v-select
									v-model="selectedCheckinTemplate"
									class="rounded-lg required"
									clearable
									:items="templates"
									item-text="name"
									label="Check-in Validation"
									outlined
									return-object
									:rules="rules.template_id"
							></v-select>
						</v-col>
					</v-row>
					<!-- End: Child Coupon Template -->

					<!-- Redemption Radius -->
					<v-row
							v-if="checkin.redemption_validation === 'gps' || checkin.redemption_validation === 'gpsOrPin'">
						<v-col>
							<v-text-field
									v-model.number="checkin.redemption_radius"
									v-two-decimals
									class="rounded-lg required"
									label="Radius"
									min="0.25"
									outlined
									persistent-hint
									suffix=" miles"
									:rules="rules.radius"
									type="number"
									@wheel.native.prevent
							></v-text-field>
							
						</v-col>
					</v-row>
					<!-- End: Child Coupon Template -->

				</ui-box>

			</v-form>

			<form-divider/>

			<v-form v-model="formIsValid.details" ref="detailsForm">

				<ui-box :form="$refs.detailsForm" :eager="!!product.id" new-forms>

					<form-title title="Check-In Details">
						Provide some information about the place they are visiting.
					</form-title>

					<form-divider inner/>

					<!-- Checkin name -->
					<v-row>
						<v-col>
							<v-text-field
									v-model="checkin.name"
									class="rounded-lg required"
									hint="Ex: Grand Peak State Park Visit, Multnomah Falls Visit."
									label="Check-in Name"
									outlined
									persistent-hint
									:rules="rules.name"
							></v-text-field>
						</v-col>
					</v-row>
					<!-- End: Admission name -->

					<!-- Description -->
					<v-row>
						<v-col>
							<wysiwyg-input
								v-model="checkin.short_information"
								:counter="1250"
								hide-details="auto"
								hint="Shown on the mobile pass."
								label="Brief Description"
								outlined
								rows="4"
							/>
						</v-col>
					</v-row>
					<v-row>
						<v-col>
							<wysiwyg-input
								v-model="checkin.restrictions"
								:counter="1250"
								hide-details="auto"
								hint="Please list any restrictions here. For example: 'Can only be used on weekends.'"
								label="Restrictions"
								outlined
								persistent-hint
								rows="4"
							></wysiwyg-input>
						</v-col>
					</v-row>
					<!-- End: Description -->

				</ui-box>

			</v-form>

			<form-divider/>

			<v-form v-model="formIsValid.repeat" ref="repeatForm" class="mb-8">

				<ui-box
						v-if="shouldShowSection('repeat')"
						:form="$refs.repeatForm"
						:eager="!!product.id"
						new-forms
				>

					<form-title title="Repeat Usage">
						Determine if the customer can check-in more than once. 
					</form-title>

					<form-divider inner/>

					<v-row>
						<v-col cols="12" md="5" lg="4">
							<v-switch
									v-model="checkin.repeat"
									:class="checkin.repeat ? 'mt-3' : 'mb-2 mt-0'"
									dense
									hide-details="auto"
									inset
									label="Is this a repeat use check-in?"
							></v-switch>
						</v-col>
						<v-col cols="12" md="7" lg="8">
							<v-row v-if="checkin.repeat">
								<v-col>
									<v-text-field
											v-model.number="checkin.repeat_delay"
											class="flex-grow-1 rounded-lg required"
											clearable
											hide-spin-buttons
											label="Repeat Delay"
											min="1"
											outlined
											prefix="Every"
											:rules="rules.repeat_delay"
											type="number"
											@wheel.native.prevent
									></v-text-field>
								</v-col>
								<v-col>
									<v-select
											v-model="checkin.repeat_delay_type"
											class="flex-grow-1 rounded-lg required"
											clearable
											:items="['hours', 'days', 'weeks', 'months']"
											label="Repeat Delay Type"
											outlined
											:rules="rules.repeat_delay_type"
									></v-select>
								</v-col>
							</v-row>
						</v-col>
					</v-row>
				</ui-box>

			</v-form>
			
			<v-form v-model="formIsValid.blackoutDates" ref="blackoutDatesForm" class="mb-8">
				<blackout-dates
						:refs="$refs"
						:product="product"
						:coupon="coupon"
						:form-is-valid="formIsValid"
						:timezone="partner?.timezone || timezone"
				/>
			</v-form>

			<v-form  v-if="origin!='merchant-portal'" v-model="formIsValid.buttonLanguage" ref="buttonLanguageForm" class="mb-8">
				<button-language :refs="$refs" :product="product" :coupon="checkin" :partner="partner" :form-is-valid="formIsValid"/>
			</v-form>

			<mobile-pass-preview-modal
					v-if="preview.url"
					:preview-url="preview.url"
					@accept-preview="acceptPreview"
					@reject-preview="rejectPreview"
			></mobile-pass-preview-modal>

			<save-form
					:refs="$refs"
					:is-saving="isSaving"
					new-forms
					save-text="Save & Preview"
					@cancel-action="cancelChanges"
					@save-action="saveChanges"
			></save-form>
		</v-col>
	</v-row>
</template>

<script>
import {checkinHelpers, productHelpers} from '../helpers';
import FormDivider from "../../../../UI/Form/FormDivider.vue";
import VenueSelect from '../Shared/VenueSelect.vue';
import BlackoutDates from "@app/Shared/Components/BlackoutDates.vue";
import ButtonLanguage from "../Shared/ButtonLanguage.vue";

export default {
	name: "CheckInProductForm",
	components: {ButtonLanguage, BlackoutDates, FormDivider, VenueSelect },
	props: {
		template: {
			type: Object,
			default: () => ({})
		},
		showSections: {
			type: Array,
			default: () => []
		},
		venue: {
			type: Object,
			default: () => ({})
		},
		venues: {
			type: Array,
			default: () => []
		},
		partner: {
			type: Object | null,
			default: null
		},
		product: {
			type: Object,
			default: () => ({})
		},
		returnUrl: {
			type: String,
			default: ''
		},
		origin: {
			type: String,
			default: ''
		},
		timezone: {
			type: String,
			default: ''
		}
	},
	data() {
		return {
			checkin: {
				...productHelpers.productFormat(this.product)
			},
			formIsValid: {
				checkinType: false,
				details: false,
			},
			isSaving: false,
			preview: {
				product: {},
				url: undefined
			},
			selectedCheckinTemplate: productHelpers.getTemplate(this.product, this.template, this.template.childTemplates.map(t => t.id)),
		}
	},
	computed: {
		rules() {
			return checkinHelpers.getValidationRules(this.$data);
		},
		templates() {
			// move this to the helpers
			const { children, childTemplates } = this.template;
			const childTemplatesArray = Object.values(childTemplates);
			const hasLimitedTemplates = childTemplatesArray.length < children.length;
			return hasLimitedTemplates ? childTemplatesArray : childTemplatesArray;
		}
	},
	mounted() {
		this.setupRedemptionValidation();
	},
	methods: {
		acceptPreview() {
			productHelpers.acceptPreview(this.checkin, 'Check-in', this.preview, this.getReturnUrl());
		},
		cancelChanges() {
			window.location = this.getReturnUrl();
		},
		getEditUrl() {
			return this.partner
					? `/partner-portal/${this.partner.id}/products/edit-product/${this.checkin.id}`
					: `/merchant-portal/${this.venue.id}/products/main/edit-product/${this.checkin.id}`;
		},
		getReturnUrl() {
			return productHelpers.getReturnUrl(this.returnUrl, this.partner, this.venue);
		},
		rejectPreview() {
			this.isSaving = false;
			this.preview.url = undefined;
		},
		saveChanges() {
			productHelpers.saveChanges(this.isSaving, this.partner, this.venue, this.checkin, this.preview);
		},
		shouldShowSection(section) {
			return checkinHelpers.shouldShowSection(this.showSections, section);
		},
		setupRedemptionValidation() {
			if(this.selectedCheckinTemplate?.code === 'bw_check_in_pin') {
				this.$set(this.checkin, 'redemption_validation', 'pin');
			}
			else if(this.selectedCheckinTemplate?.code === 'bw_check_in_gps') {
				this.$set(this.checkin, 'redemption_validation', 'gps');
			}
			else if(this.selectedCheckinTemplate?.code === 'bw_check_in_gps_or_pin') {
				this.$set(this.checkin, 'redemption_validation', 'gpsOrPin');
			}
		}
	},
	watch: {
		selectedCheckinTemplate: {
			handler(newValue) {
				this.checkin.template_id = newValue?.id || this.product.template_id;
				this.setupRedemptionValidation();
			}
		}
	}
};
</script>
